import React from 'react';
import { Button, Chip } from '@mui/material';
import { DataManagementConfig } from '@shared/components/data-management/types';
import { SurveyAuditTableData } from '../types';
import { AuditUserRole } from '../dtos';
import { createReadyAuditsDataManagementHooks } from '../hooks/useReadyAuditsForDataManagement';
import { notificationService } from '@shared/components/notifications/notificationService';

/**
 * Hook return type for useReadyAuditsConfig
 */
export interface ReadyAuditsConfigResult {
  config: DataManagementConfig<SurveyAuditTableData>;
  startAuditMutation: {
    mutateAsync: (params: {
      surveyInstanceId: string;
      userRole: AuditUserRole;
      surveyId: string;
    }) => Promise<SurveyAuditTableData>;
    isPending: boolean;
  };
}

/**
 * Creates table configuration for audits with READY status
 * These are audits that have been auto-created when a survey instance received responses
 * but have not yet been started by a user
 *
 * @param subjectId - Subject ID for scoping audits
 * @param tenantId - Tenant ID for context
 * @param domainId - Domain ID for context
 * @param navigate - Optional navigation function
 * @param onStartAuditClick - Callback when start audit is clicked (for confirmation dialog)
 */
export const useReadyAuditsConfig = (
  subjectId: string,
  tenantId: string,
  domainId: string,
  navigate?: (path: string) => void,
  onStartAuditClick?: (audit: {
    id: string;
    surveyInstanceId: string;
    surveyId: string;
    surveyTitle: string;
    responseCount: number;
  }) => void
): ReadyAuditsConfigResult => {
  // Get data management hooks
  const { useReadyAuditsQuery, useStartAuditWrapper, useArchiveAuditWrapper } =
    createReadyAuditsDataManagementHooks(subjectId, tenantId, domainId);

  const startAuditMutation = useStartAuditWrapper();
  const archiveAuditMutation = useArchiveAuditWrapper();

  const config: DataManagementConfig<SurveyAuditTableData> = {
    // Data hooks
    hooks: {
      useQuery: useReadyAuditsQuery,
    },

    // Table configuration
    table: {
      title: 'Ready to Audit',
      columns: [
        // Survey column
        {
          label: 'Survey',
          key: 'surveyTitle',
        },

        // Created date column
        {
          label: 'Created',
          key: 'createdAt',
          render: (value) => {
            if (!value) return '-';
            if (typeof value === 'string' || typeof value === 'number') {
              return new Date(value).toLocaleDateString();
            }
            return '-';
          },
        },

        // Response count column
        {
          label: 'Responses',
          key: 'responseCount',
          render: (value) => value?.toString() || '0',
        },

        // Status chip column
        {
          label: 'Status',
          key: 'status',
          render: (value) => {
            const status = value as string;
            const isInProgress = status === 'IN_PROGRESS';
            return (
              <Chip
                label={isInProgress ? 'In Progress' : 'Ready to Audit'}
                color={isInProgress ? 'primary' : 'secondary'}
                variant="outlined"
                size="small"
              />
            );
          },
        },

        // Actions column with Start/Continue Audit button
        {
          label: 'Actions',
          key: 'id',
          render: (value, row) => {
            const isInProgress = row.status === 'IN_PROGRESS';
            return (
              <Button
                size="small"
                variant="contained"
                color={isInProgress ? 'primary' : 'success'}
                disabled={!row.responseCount || row.responseCount === 0}
                onClick={async (e) => {
                  e.stopPropagation();

                  // If already in progress, just navigate to it
                  if (isInProgress) {
                    if (navigate) {
                      navigate(`/app/survey-audits/${row.id}`);
                    }
                    return;
                  }

                  // Trigger confirmation dialog via callback
                  if (onStartAuditClick) {
                    onStartAuditClick({
                      id: row.id,
                      surveyInstanceId: row.surveyInstanceId,
                      surveyId: row.surveyId,
                      surveyTitle: row.surveyTitle,
                      responseCount: row.responseCount || 0,
                    });
                  }
                }}
              >
                {isInProgress ? 'Continue Audit' : 'Start Audit'}
              </Button>
            );
          },
        },
      ],
      emptyMessage: '✨ All caught up!\nAudits will show here when survey responses come in.',
      errorMessage: 'Failed to load ready audits. Please try again.',
    },

    // Row actions (context menu)
    actions: {
      custom: [
        // Archive action
        {
          label: 'Archive',
          onClick: async (row: SurveyAuditTableData) => {
            try {
              await archiveAuditMutation.mutateAsync({
                auditId: row.id,
                subjectId: row.subjectId,
              });
              notificationService.success(`Audit archived for "${row.surveyTitle}"`);
            } catch (error) {
              console.error('Failed to archive audit:', error);
              notificationService.error('Failed to archive audit. Please try again.');
            }
          },
        },
      ],
    },

    // Table features
    features: {
      search: true,
      pagination: true,
    },
  };

  return { config, startAuditMutation };
};
